import { formatClockTime, type HourCycle } from '../../../lib/timeFormat'

// Minutes per grid row. 1 means rows snap to single-minute precision so any
// odd start/end time (e.g. EDC's "10:07pm" or "2:35am") gets an integer row
// line. CSS Grid rejects fractional grid-row values and silently auto-places
// the element below the grid, so a coarser SLOT_MIN would drop those sets.
export const SLOT_MIN = 1
// Pixels per grid row. 1.7 → 51px per 30min; tweak to scale the timetable's vertical density.
export const SLOT_PX = 1.7
// Time labels and hour gridlines render every LABEL_MIN minutes, decoupled
// from row precision.
export const LABEL_MIN = 30

// Hours before the event's day cutoff are "late night" — the previous day's
// party continuing past midnight — so they sort after 23:59 in the grid.
export { festivalMinutes as timeToMinutes } from '@shared/dayCutoff'

/** The time axis' hour marker: "23" in 24h, a bare "11" in 12h — the set times beside it carry AM/PM. */
export function minutesToHourLabel(minutes: number, cycle: HourCycle = '24h'): string {
  const h = Math.floor((minutes % (24 * 60)) / 60)
  return (cycle === '24h' ? h : h % 12 || 12).toString()
}

/** Format grid-space minutes (late-night values ≥ 24h) as an H:MM clock label. */
export function minutesToClockLabel(minutes: number, cycle: HourCycle = '24h', locale = 'en_US'): string {
  const h = Math.floor(minutes / 60) % 24
  const m = String(((Math.round(minutes) % 60) + 60) % 60).padStart(2, '0')
  return cycle === '24h' ? `${h}:${m}` : formatClockTime(`${h}:${m}`, '12h', locale)
}

// ── Pixel ↔ grid-minute mapping ─────────────────────────────────────────────
// The grid's first row is a 2rem sticky stage-header band; everything below it
// is timeline. `top` values are measured from the timeline origin (below the
// header), matching the now-line layer's `gridRow: 2 / -1` placement.
//
// The *rendered* track sizes are not the ones we asked for: engines snap lengths
// to their layout unit (Chrome 1/64px), so a SLOT_PX of 1.7 lays out as 1.6875,
// and row positions accumulate that rounded value. Doing the px math with the
// requested 1.7 drifts ~9px away from the gridlines over a 12-hour day — which
// is why the now-line missed the hour mark. Read the used sizes back off the
// grid instead so the line lands on the same pixel as the gridlines and blocks.
export interface GridGeometry {
  /** Rendered height of one SLOT_MIN row. */
  slotPx: number
  /** Rendered height of the sticky stage-header row. */
  headerPx: number
}

export const DEFAULT_GRID_GEOMETRY: GridGeometry = { slotPx: SLOT_PX, headerPx: 32 }

/** Used row-track sizes of a schedule grid, or null if it isn't laid out yet. */
export function measureGridGeometry(grid: HTMLElement | null): GridGeometry | null {
  if (!grid) return null
  const [header, slot] = getComputedStyle(grid).gridTemplateRows.split(' ')
  const headerPx = parseFloat(header)
  const slotPx = parseFloat(slot)
  if (!Number.isFinite(headerPx) || !Number.isFinite(slotPx) || slotPx <= 0) return null
  return { slotPx, headerPx }
}

/** Grid-minute → pixel offset below the header band. */
export function festivalMinutesToTop(
  minutes: number,
  gridMin: number,
  slotPx: number = SLOT_PX,
): number {
  return ((minutes - gridMin) / SLOT_MIN) * slotPx
}

/** Grid-minute → zero-padded 24h 'HH:MM' (wraps past midnight), for storage and <input type="time">. */
export function minutesToTimeInput(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** Pixel Y within the grid element (header included) → grid-minute. */
export function topToFestivalMinutes(
  yWithinGrid: number,
  gridMin: number,
  geometry: GridGeometry = DEFAULT_GRID_GEOMETRY,
): number {
  return gridMin + ((yWithinGrid - geometry.headerPx) / geometry.slotPx) * SLOT_MIN
}

/** A Date → grid-minute on its festival day (late-night hours roll past 24h). */
export function festivalMinutesNow(now: Date, cutoff: number, tz?: string): number {
  const p = getPartsInZone(now, tz)
  return (p.hours < cutoff ? p.hours + 24 : p.hours) * 60 + p.minutes
}

/** Grid-minute of the now-line on `day`'s grid, or null when "now" isn't on
 *  this day (different day, or outside the set range). Pure so both the reactive
 *  now-line and the one-shot mount auto-scroll can share it. */
export function computeNowLineMinutes(
  now: Date,
  day: string,
  gridMin: number,
  gridMax: number,
  hasSets: boolean,
  cutoff: number,
  tz?: string,
): number | null {
  if (!hasSets) return null
  const p = getPartsInZone(now, tz)
  let aYear = p.year, aMonth = p.month, aDay = p.day
  if (p.hours < cutoff) {
    const prev = new Date(aYear, aMonth - 1, aDay - 1)
    aYear = prev.getFullYear(); aMonth = prev.getMonth() + 1; aDay = prev.getDate()
  }
  const todayISO = `${aYear}-${String(aMonth).padStart(2, '0')}-${String(aDay).padStart(2, '0')}`
  if (day !== todayISO) return null
  const nowMin = festivalMinutesNow(now, cutoff, tz)
  return nowMin >= gridMin && nowMin <= gridMax ? nowMin : null
}

/** Inverse of the now-line position math: turn grid-space minutes on a given
 *  festival day back into an absolute Date. Values ≥ 24h are past-midnight and
 *  roll onto the next calendar day. When `tz` is set, the result is the instant
 *  whose wall-clock time in that zone matches the target (offset-corrected). */
export function festivalMinutesToDate(minutes: number, dayISO: string, tz?: string): Date {
  const total = Math.round(minutes)
  const extraDays = Math.floor(Math.floor(total / 60) / 24)
  const hours = ((Math.floor(total / 60) % 24) + 24) % 24
  const mins = ((total % 60) + 60) % 60
  const [Y, Mo, D] = dayISO.split('-').map(Number)
  const base = new Date(Y, Mo - 1, D + extraDays)
  const yy = base.getFullYear()
  const mm = base.getMonth() + 1
  const dd = base.getDate()
  if (!tz) return new Date(yy, mm - 1, dd, hours, mins, 0, 0)
  const guess = Date.UTC(yy, mm - 1, dd, hours, mins)
  const p = getPartsInZone(new Date(guess), tz)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hours, p.minutes)
  return new Date(guess - (asUtc - guess))
}

/** Extract hours, minutes, and calendar date in a given IANA timezone.
 *  Falls back to local time when tz is undefined or the browser doesn't
 *  recognize the zone string. */
export function getPartsInZone(
  date: Date,
  tz?: string,
): { hours: number; minutes: number; year: number; month: number; day: number } {
  if (!tz) {
    return {
      hours: date.getHours(),
      minutes: date.getMinutes(),
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    }
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: 'numeric',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour12: false,
    }).formatToParts(date)
    const get = (type: string) =>
      parseInt(parts.find(p => p.type === type)?.value ?? '0', 10)
    return { hours: get('hour') % 24, minutes: get('minute'), year: get('year'), month: get('month'), day: get('day') }
  } catch {
    return {
      hours: date.getHours(),
      minutes: date.getMinutes(),
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    }
  }
}
